import { Dispatch, SetStateAction } from 'react'
import { motion } from 'framer-motion'
import styled from '@emotion/styled'
import {
  BackButton,
  BottomSection,
  Button,
  TitleSection,
} from '../../styled.components'
import { Input, Spinner } from '@nextui-org/react-latest'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { colors } from '@configs/styles/config'
import { Controller, useForm } from 'react-hook-form'
import createSupport from '@services/api/support/createSupport'
import { SupportRequest } from '@type/user'
import { useTranslation } from 'next-i18next'

type Props = {
  setCurrentStep: Dispatch<
    SetStateAction<
      'stepone' | 'steptwo' | 'stepthree' | 'stepfour' | 'stepfive'
    >
  >
  inquiryDetails: string
  inquiryType:
    | ''
    | 'introductionToEdugramFeatures'
    | 'accountSetupAndVerification'
    | 'contentCreationAndPublishingGuidelines'
    | 'earningOpportunitiesAndRevenueModels'
    | 'technicalSupportAndTroubleshooting'
    | 'feedbackAndImprovementSuggestions'
    | 'generalInquiry'
    | 'other'
  studentAudience: 'lessThen5000' | 'moreThen5000' | 'moreThen20000'
  amountOfCourses: '0to1' | '1to5' | '6plus'
}

const Container = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  max-width: 340px;
  margin: 2rem auto;
  margin-top: 0rem;

  .containerClass {
    width: 100% !important;
    height: 48px;
    border: 2px solid hsl(240 6% 90%) !important;
    :hover {
      border: 2px solid ${colors.uguDarkGrey} !important;
    }
    :focus-within {
      border: 2px solid rgba(0, 0, 0, 0.7) !important;
    }
  }
  .invalid-class {
    border: 2px solid ${colors.uguRed} !important;
  }
`
const MiddleSection = styled.div`
  display: flex;
  gap: 1.6rem;
  flex-direction: column;
  width: 100%;
`
const InputFieldWrapper = styled.div`
  /* margin-bottom: 1rem; */
  display: flex;
  flex-direction: column;
  gap: 10px;
`
const ErrorMessageWrapper = styled.div`
  font-size: 14px;
  color: ${colors.uguRed};
  font-family: var(--font-inter);
`
interface FormData {
  fullName: string
  email: string
  phoneNumber: string
}

const StepFour = ({
  setCurrentStep,
  inquiryDetails,
  inquiryType,
  amountOfCourses,
  studentAudience,
}: Props) => {
  const { t } = useTranslation(['landingPage'])
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    const supportData: SupportRequest = {
      ...data,
      type: inquiryType,
      message: inquiryDetails,
      amountOfCourses,
      studentAudience,
      phone: data.phoneNumber,
    }
    try {
      const res = await createSupport(supportData)
      if (res) {
        setCurrentStep('stepfive')
      }
    } catch (error) {}
  }
  return (
    <motion.div
      initial={{ opacity: 0, translateY: 100 }}
      animate={{
        opacity: 1,
        translateY: 0,
      }}
      transition={{
        duration: 0.3,
      }}
    >
      <Container onSubmit={handleSubmit(onSubmit)}>
        <TitleSection>{t('connectWithUs.stepFour.title')}</TitleSection>
        <MiddleSection>
          <InputFieldWrapper>
            <Input
              placeholder={t('connectWithUs.stepFour.fullNamePlaceholder')}
              className="!h-[40px]"
              variant="bordered"
              classNames={{
                input: '!text-[15px] font-[Inter] placeholder:text-uguPurple',
              }}
              size="lg"
              {...register('fullName', {
                required: t('connectWithUs.stepFour.errors.fullNameRequired'),
                minLength: {
                  value: 10,
                  message: t('connectWithUs.stepFour.errors.fullNameMinLength'),
                },
                maxLength: {
                  value: 42,
                  message: t('connectWithUs.stepFour.errors.fullNameMaxLength'),
                },
              })}
              radius="md"
              isInvalid={errors.fullName ? true : false}
            />
            {errors.fullName && (
              <ErrorMessageWrapper>
                {errors.fullName.message as string}
              </ErrorMessageWrapper>
            )}
          </InputFieldWrapper>
          <InputFieldWrapper>
            <Input
              placeholder={t('connectWithUs.stepFour.emailPlaceholder')}
              className="h-[40px]"
              variant="bordered"
              classNames={{
                input: '!text-[15px] font-[Inter] placeholder:text-uguPurple',
              }}
              radius="md"
              size="lg"
              {...register('email', {
                required: t('connectWithUs.stepFour.errors.emailRequired'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: t('connectWithUs.stepFour.errors.emailInvalid'),
                },
                maxLength: {
                  value: 42,
                  message: t('connectWithUs.stepFour.errors.emailMaxLength'),
                },
              })}
              isInvalid={errors.email ? true : false}
            />
            {errors.email && (
              <ErrorMessageWrapper>
                {errors.email.message as string}
              </ErrorMessageWrapper>
            )}
          </InputFieldWrapper>
          <InputFieldWrapper>
            <Controller
              render={({ field }) => (
                <PhoneInput
                  country={'us'}
                  {...field}
                  containerClass={`containerClass shadow-sm ${
                    errors.phoneNumber ? 'invalid-class' : ''
                  }`}
                  buttonClass={'buttonClass'}
                  inputClass={'inputClass'}
                  inputProps={{
                    required: true,
                    name: 'phoneNumber',
                  }}
                />
              )}
              control={control}
              name="phoneNumber"
              rules={{
                required: t(
                  'connectWithUs.stepFour.errors.phoneNumberRequired'
                ),
                minLength: {
                  value: 7,
                  message: t(
                    'connectWithUs.stepFour.errors.phoneNumberMinLength'
                  ),
                },
              }}
            />
            {errors.phoneNumber && (
              <ErrorMessageWrapper className="-mt-1.5">
                {errors.phoneNumber.message as string}
              </ErrorMessageWrapper>
            )}
          </InputFieldWrapper>
        </MiddleSection>
        <BottomSection>
          <BackButton onClick={() => setCurrentStep('stepthree')}>
            {t('connectWithUs.stepFour.goBack')}
          </BackButton>
          <Button type={'submit'} disabled={isSubmitting}>
            {isSubmitting ? (
              <Spinner color="white" size={'sm'} />
            ) : (
              t('connectWithUs.stepFour.submit')
            )}
          </Button>
        </BottomSection>
      </Container>
    </motion.div>
  )
}

export default StepFour
